import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, ShieldCheck, PieChart } from 'lucide-react';
import AnimatedBadge from '../ui/AnimatedBadge';

import { useContact } from '../../context/ContactContext';

export default function IrpfConsultoriaSection() {
  const { openModal } = useContact();
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  });

  // Parallax effect for the background image
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);

  return (
    <section 
      ref={containerRef}
      id="irpf" 
      className="w-full bg-white py-24 relative overflow-hidden"
    >
      {/* Background Image with Parallax */}
      <motion.div 
        style={{ y }}
        className="absolute inset-0 w-full h-[120%] pointer-events-none opacity-5"
      >
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1497366811353-6870744d04b2?auto=format&fit=crop&q=80')] bg-cover bg-center"></div>
      </motion.div>

      <div className="max-w-7xl mx-auto px-6 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-20 items-center">
          
          {/* Content Left */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-7 text-center lg:text-left"
          >
            <div className="mb-8 flex justify-center lg:justify-start">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-orange-500/40 text-orange-600 font-bold text-[10px] uppercase tracking-widest">
                Blindagem Patrimonial
              </div>
            </div>
            
            <h2 className="text-2xl md:text-5xl font-black text-slate-900 tracking-tight leading-tight mb-6 md:mb-8 text-center lg:text-left px-4 md:px-0">
              Blindagem Patrimonial <br className="hidden md:block" /> 
              <span className="text-orange-600">Estratégica.</span>
            </h2>
            
            <p className="text-sm md:text-xl text-slate-600 font-medium leading-relaxed max-w-2xl mb-8 md:mb-12 mx-auto lg:mx-0 text-center lg:text-left px-4 md:px-0">
              Transformamos a complexidade do IRPF em uma estratégia de crescimento. 
              Nossa consultoria protege seus ativos e planeja o futuro com inteligência fiscal absoluta.
            </p>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 md:gap-12 mb-12 text-left">
              <div className="flex gap-4">
                <div className="w-14 h-14 bg-slate-50 rounded-2xl flex items-center justify-center shrink-0 border border-slate-100">
                  <ShieldCheck className="text-orange-500" size={28} />
                </div>
                <div>
                  <h4 className="text-slate-900 font-bold text-lg mb-1">Proteção de Ativos</h4>
                  <p className="text-slate-500 text-sm leading-relaxed">Estratégias societárias para blindagem do patrimônio familiar.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-14 h-14 bg-slate-50 rounded-2xl flex items-center justify-center shrink-0 border border-slate-100">
                  <PieChart className="text-orange-500" size={28} />
                </div>
                <div>
                  <h4 className="text-slate-900 font-bold text-lg mb-1">Planejamento IRPF</h4>
                  <p className="text-slate-500 text-sm leading-relaxed">Redução legal de carga tributária e análise prévia de risco.</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Form Right */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="lg:col-span-5"
          >
            <div className="bg-slate-50 border border-slate-200 rounded-[2.5rem] md:rounded-[3rem] p-8 md:p-12 shadow-xl relative overflow-hidden mx-auto max-w-md lg:max-w-none">
              <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">Consultoria Especializada</h3>
              <p className="text-slate-500 text-sm mb-10">Fale com um especialista em blindagem patrimonial.</p>
              
              <form className="space-y-6">
                <div className="space-y-2 text-left">
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Nome Completo</label>
                  <input type="text" placeholder="Ex: Ricardo Costa" className="w-full bg-white border border-slate-200 rounded-2xl px-5 py-4 text-slate-900 placeholder-slate-300 focus:outline-none focus:border-orange-500 transition-all" />
                </div>
                <div className="space-y-2 text-left">
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">WhatsApp de Contato</label>
                  <input type="tel" placeholder="(00) 00000-0000" className="w-full bg-white border border-slate-200 rounded-2xl px-5 py-4 text-slate-900 placeholder-slate-300 focus:outline-none focus:border-orange-500 transition-all" />
                </div>
                <a 
                  href="https://wa.me/5511949830659"
                  rel="noopener noreferrer"
                  className="w-full py-6 bg-orange-600 hover:bg-orange-500 text-white font-black rounded-2xl transition-all duration-300 uppercase tracking-widest text-[10px] mt-4 shadow-lg shadow-orange-600/20 flex items-center justify-center gap-2"
                >
                  Solicitar Diagnóstico <ArrowRight size={18} />
                </a>
              </form>
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
}
